/**
 * Таблица с клавиатурной навигацией — основа всех повторяющихся блоков
 * (ТЗ FR-11, п. 8.4).
 *
 * Требование звучит скромно, но именно от него зависит, окупится ли проект:
 * юрист заполняет кредиторов, детей, шесть разделов имущества и сделки.
 * Если для каждой строки нужно тянуться к мыши, экономия времени исчезает.
 *
 * Клавиши:
 *   Tab / Shift+Tab  — следующая и предыдущая ячейка, на краю строки переход
 *                      на соседнюю строку;
 *   Enter            — новая строка (в последней строке) либо переход вниз;
 *   ↑ ↓              — строка выше и ниже в той же графе;
 *   Ctrl+Enter       — вставить строку сразу после текущей;
 *   Ctrl+Delete      — удалить текущую строку;
 *   Escape           — отменить правку ячейки.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

export interface ColumnDef<Row> {
  key: string;
  title: string;
  /** Ширина в процентах */
  width?: number;
  /** Тип ячейки: обычный ввод, число, дата, признак, выбор из списка */
  type?: 'text' | 'money' | 'number' | 'date' | 'checkbox' | 'select';
  options?: string[];
  /** Чтение значения из строки */
  get: (row: Row) => string | number | boolean;
  /** Запись значения в строку */
  set: (row: Row, value: string) => Row;
  /** Собственная отрисовка ячейки — для полей с подсказками */
  render?: (props: { row: Row; rowIndex: number; onChange: (row: Row) => void; onKeyDown: (e: React.KeyboardEvent) => void; readOnly: boolean }) => ReactNode;
  /** Ячейка не редактируется (например, расчётный итог) */
  readOnly?: boolean;
  align?: 'left' | 'right' | 'center';
}

export interface DataGridProps<Row> {
  rows: Row[];
  columns: Array<ColumnDef<Row>>;
  onChange: (rows: Row[]) => void;
  /** Создание пустой строки */
  createRow: () => Row;
  getRowId: (row: Row) => string;
  readOnly?: boolean;
  /** Сообщение при пустой таблице */
  emptyText?: string;
  /** Итоговая строка под таблицей */
  footer?: ReactNode;
  /** Ошибки по строкам: индекс строки → ключи граф с ошибкой */
  errors?: Record<number, string[]>;
}

export function DataGrid<Row>({
  rows,
  columns,
  onChange,
  createRow,
  getRowId,
  readOnly = false,
  emptyText = 'Строк нет. Нажмите «Добавить строку» или Enter в таблице.',
  footer,
  errors = {},
}: DataGridProps<Row>) {
  const tableRef = useRef<HTMLTableElement>(null);
  /** Куда поставить курсор после перерисовки: строка и графа */
  const [focusTarget, setFocusTarget] = useState<{ row: number; col: number } | null>(null);

  const focusCell = useCallback((rowIndex: number, colIndex: number) => {
    const selector = `[data-cell="${rowIndex}-${colIndex}"] input, [data-cell="${rowIndex}-${colIndex}"] select`;
    const element = tableRef.current?.querySelector<HTMLElement>(selector);
    element?.focus();
    if (element instanceof HTMLInputElement && element.type === 'text') element.select();
  }, []);

  // Перевод курсора выполняется после отрисовки: новая строка ещё не в DOM
  // в момент нажатия Enter.
  useEffect(() => {
    if (!focusTarget) return;
    focusCell(focusTarget.row, focusTarget.col);
    setFocusTarget(null);
  }, [focusTarget, focusCell]);

  const updateRow = (index: number, row: Row) => {
    const next = rows.slice();
    next[index] = row;
    onChange(next);
  };

  const insertRow = (afterIndex: number) => {
    const next = rows.slice();
    next.splice(afterIndex + 1, 0, createRow());
    onChange(next);
    setFocusTarget({ row: afterIndex + 1, col: 0 });
  };

  const removeRow = (index: number) => {
    const next = rows.slice();
    next.splice(index, 1);
    onChange(next);
    setFocusTarget({ row: Math.max(0, index - 1), col: 0 });
  };

  const handleKeyDown = (event: React.KeyboardEvent, rowIndex: number, colIndex: number) => {
    if (readOnly) return;

    // Ctrl+Enter — вставка строки, Ctrl+Delete — удаление.
    if (event.ctrlKey && event.key === 'Enter') {
      event.preventDefault();
      insertRow(rowIndex);
      return;
    }
    if (event.ctrlKey && (event.key === 'Delete' || event.key === 'Backspace')) {
      event.preventDefault();
      removeRow(rowIndex);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (rowIndex === rows.length - 1) insertRow(rowIndex);
      else setFocusTarget({ row: rowIndex + 1, col: colIndex });
      return;
    }

    if (event.key === 'ArrowDown' && rowIndex < rows.length - 1) {
      event.preventDefault();
      setFocusTarget({ row: rowIndex + 1, col: colIndex });
      return;
    }
    if (event.key === 'ArrowUp' && rowIndex > 0) {
      event.preventDefault();
      setFocusTarget({ row: rowIndex - 1, col: colIndex });
      return;
    }

    if (event.key === 'Tab') {
      // На краю строки Tab переносит на соседнюю строку, а не выбрасывает
      // из таблицы: иначе ввод прерывается на каждой строке.
      const last = columns.length - 1;
      if (!event.shiftKey && colIndex === last && rowIndex < rows.length - 1) {
        event.preventDefault();
        setFocusTarget({ row: rowIndex + 1, col: 0 });
      } else if (event.shiftKey && colIndex === 0 && rowIndex > 0) {
        event.preventDefault();
        setFocusTarget({ row: rowIndex - 1, col: last });
      }
    }
  };

  return (
    <div className="grid">
      <table ref={tableRef} className="grid-table">
        <thead>
          <tr>
            <th className="grid-num">№</th>
            {columns.map((column) => (
              <th key={column.key} style={column.width ? { width: `${column.width}%` } : undefined}>
                {column.title}
              </th>
            ))}
            <th className="grid-actions" aria-label="Действия со строкой" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length + 2} className="grid-empty">
                {emptyText}
              </td>
            </tr>
          )}

          {rows.map((row, rowIndex) => {
            const rowErrors = errors[rowIndex] ?? [];
            return (
              <tr key={getRowId(row)} className={rowErrors.length ? 'grid-row-error' : undefined}>
                <td className="grid-num">{rowIndex + 1}</td>

                {columns.map((column, colIndex) => {
                  const invalid = rowErrors.includes(column.key);
                  const cellReadOnly = readOnly || column.readOnly === true;

                  return (
                    <td
                      key={column.key}
                      data-cell={`${rowIndex}-${colIndex}`}
                      className={invalid ? 'grid-cell-error' : undefined}
                      style={{ textAlign: column.align ?? 'left' }}
                    >
                      {column.render ? (
                        column.render({
                          row,
                          rowIndex,
                          onChange: (updated) => updateRow(rowIndex, updated),
                          onKeyDown: (event) => handleKeyDown(event, rowIndex, colIndex),
                          readOnly: cellReadOnly,
                        })
                      ) : column.type === 'checkbox' ? (
                        <input
                          type="checkbox"
                          checked={Boolean(column.get(row))}
                          disabled={cellReadOnly}
                          onChange={(e) => updateRow(rowIndex, column.set(row, e.target.checked ? 'true' : ''))}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                        />
                      ) : column.type === 'select' ? (
                        <select
                          value={String(column.get(row) ?? '')}
                          disabled={cellReadOnly}
                          onChange={(e) => updateRow(rowIndex, column.set(row, e.target.value))}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                        >
                          <option value="" />
                          {(column.options ?? []).map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={column.type === 'date' ? 'date' : 'text'}
                          inputMode={column.type === 'money' || column.type === 'number' ? 'decimal' : undefined}
                          value={String(column.get(row) ?? '')}
                          readOnly={cellReadOnly}
                          onChange={(e) => updateRow(rowIndex, column.set(row, e.target.value))}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                        />
                      )}
                    </td>
                  );
                })}

                <td className="grid-actions">
                  <button
                    type="button"
                    className="link-button"
                    title="Удалить строку (Ctrl+Delete)"
                    disabled={readOnly}
                    onClick={() => removeRow(rowIndex)}
                  >
                    ×
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="grid-footer">
        <button type="button" className="link-button" disabled={readOnly} onClick={() => insertRow(rows.length - 1)}>
          + Добавить строку
        </button>
        <span className="hint">Tab — следующая графа, Enter — новая строка, Ctrl+Delete — удалить</span>
        {footer}
      </div>
    </div>
  );
}
